/** Dry-run terminal flow simulator for admin (Phase 2). No API writes. */
import { todayISO } from './utils.js';
import { findTerminalFlow, findCatalogStep } from './terminal-flow-catalog.js';
import { setTerminalFlowOverrides } from './terminal-flow-merge.js';
import { describeValidationRule } from './terminal-flow-validation.js';
import {
  initNewTicketData,
  activeNewTicketSteps,
  applyNewTicketStep,
  isSkip,
  isYes,
  isNo,
} from './assistant-ticket-new.js';
import { initVisaData, activeVisaSteps, buildVisaLeadBody } from './assistant-visa-new.js';
import { initTourData, activeTourSteps, buildTourLeadBody } from './assistant-tour-new.js';
import {
  activeStageSteps,
  buildStatusPatchBody,
  validateStageAnswers,
} from './assistant-stages.js';
import {
  buildSimulatorMockLead,
  listSimulatorMockPresets,
  suggestAnswerForStep,
  pickCapturePreview,
  getPresetHappyOverrides,
} from './terminal-flow-sim-presets.js';
import { findCustomStepByKey } from './terminal-flow-custom.js';
import { suggestHandlerAnswer } from './terminal-flow-handlers.js';
import { buildNewTicketLeadBody } from './assistant-ticket-new.js';

const TZ = 'Asia/Colombo';

function deepClone(v) {
  return JSON.parse(JSON.stringify(v));
}

function buildHelpers(settings = {}) {
  const sources = settings.sources?.length
    ? settings.sources
    : ['Call', 'WhatsApp', 'Walk-in', 'Facebook'];
  const matchSource = (raw) => {
    const s = String(raw || '').trim();
    const num = Number(s);
    if (Number.isInteger(num) && num >= 1 && num <= sources.length) return sources[num - 1];
    const exact = sources.find((x) => x.toLowerCase() === s.toLowerCase());
    return exact || s;
  };
  const formatSourceQuestion = () => {
    const list = sources.slice(0, 8).map((src, i) => `${i + 1}. ${src}`).join('  ');
    return `Lead source?\n${list}`;
  };
  const defaultFollowupISO = () => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toLocaleDateString('en-CA', { timeZone: TZ });
  };
  const normalizePhone = (raw) => {
    let s = String(raw || '').trim();
    if (!s) return '';
    if (!s.startsWith('+')) {
      if (/^0/.test(s)) s = '+94 ' + s.replace(/^0+/, '');
      else s = '+94 ' + s;
    }
    return s.replace(/\s+/g, ' ').trim();
  };
  return {
    sources,
    matchSource,
    formatSourceQuestion,
    normalizePhone,
    defaultFollowupISO,
    visaPackages: settings.visas?.length
      ? settings.visas
      : ['India eVisa', 'Malaysia eVisa', 'Vietnam eVisa', 'Dubai Visa'],
    visaCosts: settings.visa_costs || {},
    tourPackages: settings.tours?.length
      ? settings.tours
      : ['Vietnam', 'India', 'Dubai', 'Bangkok', 'Singapore', 'Malaysia'],
  };
}

function captureTypeFromFlow(flow) {
  return flow.type;
}

function getRuntimeSteps(session) {
  const { flow, data, lead, stageAnswers } = session;
  if (flow.mode === 'new') {
    const type = captureTypeFromFlow(flow);
    if (type === 'visa') return activeVisaSteps(data, session.helpers);
    if (type === 'tour') return activeTourSteps(data, session.helpers);
    return activeNewTicketSteps(data, session.helpers);
  }
  return activeStageSteps(flow.type, flow.status, lead, stageAnswers);
}

function flattenChanges(before, after, prefix = '', out = []) {
  if (before === after) return out;
  if (before == null || after == null || typeof before !== 'object' || typeof after !== 'object') {
    out.push({ path: prefix || '(root)', before, after });
    return out;
  }
  if (Array.isArray(before) || Array.isArray(after)) {
    if (JSON.stringify(before) !== JSON.stringify(after)) {
      out.push({ path: prefix || '(root)', before, after });
    }
    return out;
  }
  const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
  keys.forEach((key) => {
    if (key.startsWith('_')) return;
    const path = prefix ? `${prefix}.${key}` : key;
    flattenChanges(before[key], after[key], path, out);
  });
  return out;
}

function catalogMeta(flowId, stepKey) {
  const meta = findCatalogStep(flowId, stepKey);
  if (!meta) return null;
  return {
    writesTo: meta.writesTo || '',
    impact: meta.impact || '',
    showsWhen: meta.showsWhen || '',
  };
}

function applyCaptureStep(data, step, text) {
  if (step.optional && isSkip(text)) {
    const val = step.parse ? step.parse(text, data) : null;
    step.apply(val);
    return {};
  }
  const err = step.validate ? step.validate(text, data) : null;
  if (err) return { error: err };
  const val = step.parse ? step.parse(text, data) : text.trim();
  step.apply(val);
  return {};
}

function submitNewAnswer(session, text) {
  const steps = getRuntimeSteps(session);
  const step = steps[session.stepIndex];
  if (!step) return { done: true };

  const captureType = captureTypeFromFlow(session.flow);
  const beforeData = deepClone(session.data);
  let parsed = null;

  if (captureType === 'ticket') {
    if (step.optional && isSkip(text)) {
      parsed = step.parse ? step.parse(text) : null;
      step.apply(parsed);
    } else {
      const err = step.validate ? step.validate(text, session.data) : null;
      if (err) return { error: err, stepKey: step.key };
      parsed = step.parse ? step.parse(text) : text.trim();
      const result = applyNewTicketStep(session.data, step, text, session.helpers);
      if (result?.error) {
        let jumpIndex = session.stepIndex;
        if (step.key.includes(':fare_confirm')) {
          const costKey = step.key.replace(':fare_confirm', ':cost');
          const idx = getRuntimeSteps(session).findIndex((st) => st.key === costKey);
          if (idx >= 0) jumpIndex = idx;
        } else if (step.key.startsWith('shared:') && step.key.endsWith(':confirm')) {
          const costKey = step.key.replace(':confirm', ':cost');
          const idx = getRuntimeSteps(session).findIndex((st) => st.key === costKey);
          if (idx >= 0) jumpIndex = idx;
        }
        session.stepIndex = jumpIndex;
        return { error: result.error, stepKey: step.key, reask: true };
      }
    }
  } else {
    const result = applyCaptureStep(session.data, step, text);
    if (result.error) return { error: result.error, stepKey: step.key };
    parsed = step.parse ? step.parse(text, session.data) : text.trim();
  }

  if (session.data._jumpToKey) {
    const key = session.data._jumpToKey;
    delete session.data._jumpToKey;
    const idx = getRuntimeSteps(session).findIndex((st) => st.key === key);
    if (idx >= 0) session.stepIndex = idx;
    else session.stepIndex += 1;
  } else {
    session.stepIndex += 1;
  }

  const changes = flattenChanges(beforeData, session.data).slice(0, 8);
  return {
    ok: true,
    stepKey: step.key,
    parsed,
    changes,
    meta: catalogMeta(session.flow.id, step.key),
  };
}

function submitStageAnswer(session, text) {
  const steps = getRuntimeSteps(session);
  const step = steps[session.stepIndex];
  if (!step) return { done: true };

  const useDefault = !String(text).trim() && typeof step.default === 'function';
  const value = useDefault ? String(step.default(session.stageAnswers, session.lead) ?? '') : text;

  if (step.optional && (isSkip(value) || (step.key === '_intro' && !value.trim()))) {
    session.stageAnswers[step.key] = step.parse ? step.parse(value, session.stageAnswers, session.lead) : null;
    session.stepIndex += 1;
    return {
      ok: true,
      stepKey: step.key,
      parsed: session.stageAnswers[step.key],
      changes: [{ path: `stageAnswers.${step.key}`, before: null, after: session.stageAnswers[step.key] }],
      meta: catalogMeta(session.flow.id, step.key),
      skipped: true,
    };
  }

  const err = step.validate ? step.validate(value, session.stageAnswers, session.lead) : null;
  if (err) return { error: err, stepKey: step.key };

  const parsed = step.parse
    ? step.parse(value, session.stageAnswers, session.lead)
    : value.trim();
  session.stageAnswers[step.key] = parsed;
  session.stepIndex += 1;

  return {
    ok: true,
    stepKey: step.key,
    parsed,
    changes: [{ path: `stageAnswers.${step.key}`, before: null, after: parsed }],
    meta: catalogMeta(session.flow.id, step.key),
  };
}

function buildCompletionPreview(session) {
  if (session.flow.mode === 'new') {
    const agentId = 'sim-agent';
    const type = captureTypeFromFlow(session.flow);
    if (type === 'visa') return { kind: 'lead_body', body: buildVisaLeadBody(session.data, agentId, session.helpers) };
    if (type === 'tour') return { kind: 'lead_body', body: buildTourLeadBody(session.data, agentId, session.helpers) };
    if (type === 'ticket') {
      return { kind: 'lead_body', body: buildNewTicketLeadBody(session.data, agentId, session.helpers) };
    }
    return { kind: 'lead_body', note: 'Lead body unavailable for this type.' };
  }
  const patch = buildStatusPatchBody(session.flow.type, session.flow.status, session.lead, session.stageAnswers);
  const validationError = validateStageAnswers(session.flow.type, session.flow.status, session.lead, session.stageAnswers);
  return { kind: 'status_patch', body: patch, validationError };
}

/** @param {string} flowId @param {{ settings?: object, overrides?: object }} options */
export function createSimulatorSession(flowId, options = {}) {
  const flow = findTerminalFlow(flowId);
  if (!flow) return null;

  if (options.overrides) setTerminalFlowOverrides(options.overrides);

  const helpers = buildHelpers(options.settings || {});
  const session = {
    flow,
    flowId,
    helpers,
    mockPresetId: options.mockPresetId || 'default',
    stepIndex: 0,
    history: [],
    done: false,
    completion: null,
    data: null,
    lead: null,
    stageAnswers: {},
  };

  if (flow.mode === 'new') {
    const type = captureTypeFromFlow(flow);
    if (type === 'visa') session.data = initVisaData(helpers);
    else if (type === 'tour') session.data = initTourData(helpers);
    else session.data = initNewTicketData();
  } else {
    session.lead = buildSimulatorMockLead(flow, session.mockPresetId);
    session.stageAnswers = {};
  }

  const steps = getRuntimeSteps(session);
  if (!steps.length) {
    session.done = true;
    session.completion = { empty: true, message: 'This flow has no conversational steps at this status.' };
  }

  return session;
}

export function getSimulatorQuestion(session) {
  if (!session || session.done) return null;
  const steps = getRuntimeSteps(session);
  const step = steps[session.stepIndex];
  if (!step) {
    session.done = true;
    session.completion = buildCompletionPreview(session);
    return null;
  }

  const question = session.flow.mode === 'new'
    ? (typeof step.ask === 'function' ? step.ask(session.data) : step.ask)
    : (typeof step.ask === 'function' ? step.ask(session.stageAnswers, session.lead) : step.ask);

  const hint = step.hint || '';
  const def = session.flow.mode === 'stage' && typeof step.default === 'function'
    ? step.default(session.stageAnswers, session.lead)
    : null;

  return {
    stepKey: step.key,
    stepIndex: session.stepIndex,
    totalSteps: steps.length,
    optional: !!step.optional,
    question,
    hint,
    defaultValue: def != null && def !== '' ? def : null,
    meta: catalogMeta(session.flow.id, step.key),
    validationLabel: step._validationRule ? describeValidationRule(step._validationRule) : null,
  };
}

export function submitSimulatorAnswer(session, text) {
  if (!session || session.done) return { error: 'Simulation finished — restart to try again.' };

  const q = getSimulatorQuestion(session);
  if (!q) return { done: true, completion: session.completion };

  const result = session.flow.mode === 'new'
    ? submitNewAnswer(session, text)
    : submitStageAnswer(session, text);

  if (result.error) {
    session.history.push({
      stepKey: result.stepKey || q.stepKey,
      answer: text,
      error: result.error,
    });
    return { error: result.error, stepKey: result.stepKey || q.stepKey, reask: !!result.reask };
  }

  if (result.done) {
    session.done = true;
    session.completion = buildCompletionPreview(session);
    session.history.push({ done: true });
    return { done: true, completion: session.completion };
  }

  session.history.push({
    stepKey: result.stepKey,
    answer: text,
    parsed: result.parsed,
    changes: result.changes,
    meta: result.meta,
    skipped: result.skipped,
  });

  const steps = getRuntimeSteps(session);
  if (session.stepIndex >= steps.length) {
    session.done = true;
    session.completion = buildCompletionPreview(session);
    return { ok: true, completion: session.completion, done: true };
  }

  return { ok: true, impact: result };
}

export function getSimulatorComparePreview(session) {
  if (!session || session.done) return session?.completion || null;
  if (session.flow.mode === 'new') {
    return {
      kind: 'capture_state',
      label: 'Inquiry data so far (would become lead on save)',
      body: pickCapturePreview(session.data),
    };
  }
  const patch = buildStatusPatchBody(session.flow.type, session.flow.status, session.lead, session.stageAnswers);
  const validationError = validateStageAnswers(
    session.flow.type,
    session.flow.status,
    session.lead,
    session.stageAnswers,
  );
  return {
    kind: 'status_patch',
    label: 'Status PATCH if saved now',
    body: patch,
    validationError: validationError || null,
  };
}

export function isSimulatorConfirmStep(stepKey) {
  if (!stepKey || stepKey === '_intro') return false;
  if (stepKey === 'passenger_summary') return true;
  if (stepKey.endsWith(':fare_confirm')) return true;
  if (stepKey.startsWith('shared:') && stepKey.endsWith(':confirm')) return true;
  return false;
}

function presetOverridesForSession(session) {
  return getPresetHappyOverrides(session?.flowId, session?.mockPresetId);
}

export function getSimulatorWordingCompare(session) {
  if (!session || session.done) return null;
  const q = getSimulatorQuestion(session);
  if (!q) return null;
  const catalog = findCatalogStep(session.flow.id, q.stepKey);
  const baseAsk = catalog?.ask || '';
  const baseHint = catalog?.hint || '';
  const baseChoices = catalog?.choices || '';
  const mergedAsk = q.question || '';
  const mergedHint = q.hint || '';
  const catalogHint = [baseHint, baseChoices].filter(Boolean).join('\n').trim();
  const mergedFullHint = mergedHint.trim();
  const askChanged = baseAsk && mergedAsk && baseAsk.trim() !== mergedAsk.trim();
  const hintChanged = catalogHint && mergedFullHint && catalogHint !== mergedFullHint;
  const dynamicAsk = typeof catalog?.ask === 'string' && mergedAsk.includes('\n') && !askChanged;
  return {
    stepKey: q.stepKey,
    ask: {
      base: baseAsk || '(dynamic)',
      merged: mergedAsk,
      changed: askChanged,
      dynamic: dynamicAsk,
    },
    hint: {
      base: catalogHint || '',
      merged: mergedFullHint,
      changed: hintChanged,
    },
    hasOverride: askChanged || hintChanged,
  };
}

export function formatWordingCompareText(compare) {
  if (!compare) return 'No active step.';
  if (!compare.hasOverride && !compare.ask.dynamic) {
    return 'Wording matches catalog defaults for this step.';
  }
  const lines = [];
  if (compare.ask.changed) {
    lines.push('Question (catalog):');
    lines.push(compare.ask.base);
    lines.push('\nQuestion (live):');
    lines.push(compare.ask.merged);
  } else if (compare.ask.dynamic) {
    lines.push('Question expands at runtime (e.g. numbered source list).');
    lines.push(`Catalog base: ${compare.ask.base}`);
    lines.push(`Live: ${compare.ask.merged.split('\n')[0]}…`);
  }
  if (compare.hint.changed) {
    lines.push('\nHint (catalog):');
    lines.push(compare.hint.base || '—');
    lines.push('\nHint (live):');
    lines.push(compare.hint.merged || '—');
  }
  return lines.join('\n') || 'Override active on this step.';
}

export function suggestSimulatorAnswer(session, mode = 'happy') {
  const q = getSimulatorQuestion(session);
  if (!q) return null;
  const custom = findCustomStepByKey(session.flowId, q.stepKey);
  if (custom) return suggestHandlerAnswer(custom.handler, custom, mode, q);
  return suggestAnswerForStep(q.stepKey, q, mode, presetOverridesForSession(session));
}

export { listSimulatorMockPresets };

export function formatComparePreview(preview) {
  if (!preview) return '';
  if (preview.kind === 'capture_state' || preview.kind === 'status_patch' || preview.kind === 'lead_body') {
    const lines = [preview.label ? `${preview.label}:` : ''];
    lines.push(JSON.stringify(preview.body, null, 2));
    if (preview.validationError) lines.push(`\nValidation: ${preview.validationError}`);
    else if (preview.kind === 'status_patch') lines.push('\nValidation: OK (so far)');
    return lines.join('\n');
  }
  return formatSimulatorPreview(preview);
}

/** Auto-advance with happy-path answers. Returns { steps, error, completion, paused?, pauseStep?, pauseAnswer? }. */
export function runSimulatorHappyPath(session, options = {}) {
  const maxSteps = options.maxSteps ?? 120;
  const pauseOnConfirm = options.pauseOnConfirm ?? false;
  const log = [];
  for (let i = 0; i < maxSteps; i += 1) {
    if (session.done) break;
    const q = getSimulatorQuestion(session);
    if (!q) break;
    const answer = suggestSimulatorAnswer(session, 'happy');
    if (answer == null) {
      return { steps: log, error: `No happy-path answer for step ${q.stepKey}`, completion: null };
    }
    if (pauseOnConfirm && isSimulatorConfirmStep(q.stepKey)) {
      return {
        steps: log,
        paused: true,
        pauseStep: q,
        pauseAnswer: answer,
        error: null,
        completion: null,
      };
    }
    const result = submitSimulatorAnswer(session, answer);
    log.push({ stepKey: q.stepKey, answer, ok: !result.error, error: result.error || null });
    if (result.error) {
      return { steps: log, error: result.error, completion: null };
    }
    if (result.done) {
      return { steps: log, error: null, completion: session.completion };
    }
  }
  if (!session.done) {
    return { steps: log, error: 'Happy path stopped — step limit reached', completion: null };
  }
  return { steps: log, error: null, completion: session.completion };
}

export function resetSimulatorSession(session, options = {}) {
  if (!session) return null;
  return createSimulatorSession(session.flowId, {
    settings: session.helpers ? {
      sources: session.helpers.sources,
      visas: session.helpers.visaPackages,
      visa_costs: session.helpers.visaCosts,
      tours: session.helpers.tourPackages,
    } : {},
    overrides: options.overrides,
    mockPresetId: options.mockPresetId ?? session.mockPresetId,
  });
}

export function formatSimulatorPreview(completion) {
  if (!completion) return '';
  if (completion.empty) return completion.message;
  if (completion.kind === 'lead_body') {
    return JSON.stringify(completion.body, null, 2);
  }
  if (completion.kind === 'status_patch') {
    const lines = [`PATCH preview:\n${JSON.stringify(completion.body, null, 2)}`];
    if (completion.validationError) lines.push(`\nValidation: ${completion.validationError}`);
    else lines.push('\nValidation: OK');
    return lines.join('');
  }
  return '';
}
